import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Plus, Loader2, X, DollarSign, FileText, CheckCircle2, AlertTriangle, Clock, Search as SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';
import { CompanyPicker, JobOrderPicker } from './CRMPickers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  'Not Ready': 'Not Ready to Invoice', 'Ready to Invoice': 'Ready to Invoice', Invoiced: 'Invoiced',
  'Partially Paid': 'Partially Paid', Paid: 'Paid', Overdue: 'Overdue',
};
const STATUS_COLORS = {
  'Not Ready': 'bg-gray-100 text-gray-700', 'Ready to Invoice': 'bg-blue-100 text-blue-700', Invoiced: 'bg-indigo-100 text-indigo-700',
  'Partially Paid': 'bg-amber-100 text-amber-700', Paid: 'bg-emerald-100 text-emerald-700', Overdue: 'bg-red-100 text-red-700',
};
const OPEN_STATUSES = ['Invoiced', 'Partially Paid', 'Overdue'];
const QUICK_FILTERS = [
  { key: 'all', label: 'All Revenue' }, { key: 'Ready to Invoice', label: 'Ready to Invoice' }, { key: 'Invoiced', label: 'Invoiced' },
  { key: 'Partially Paid', label: 'Partially Paid' }, { key: 'Paid', label: 'Paid' },
  { key: 'outstanding', label: 'Outstanding' }, { key: 'overdue', label: 'Overdue' },
];

const today = () => new Date().toISOString().slice(0, 10);
const outstandingOf = (r) => Math.max((r.amount_invoiced || 0) - (r.amount_collected || 0), 0);
const isOverdue = (r) => !!r.payment_due_date && r.payment_due_date < today() && outstandingOf(r) > 0 && r.invoice_status !== 'Paid';
const displayStatus = (r) => (isOverdue(r) ? 'Overdue' : r.invoice_status || 'Not Ready');
const agingBucket = (r) => {
  if (!isOverdue(r)) return null;
  const days = Math.floor((Date.now() - new Date(r.payment_due_date).getTime()) / 86400000);
  if (days <= 30) return '1-30d';
  if (days <= 60) return '31-60d';
  if (days <= 90) return '61-90d';
  return '90+d';
};
const fmt$ = (n) => `$${Math.round(n || 0).toLocaleString()}`;

export const RevenueStatusBadge = ({ record }) => {
  const s = displayStatus(record);
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[s] || 'bg-gray-100 text-gray-700'}`}>{STATUS_LABELS[s] || s}</span>;
};

const CRMRevenue = ({ embedded = false, navParams = {} }) => {
  const [config, setConfig] = useState({ invoice_statuses: [] });
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickFilter, setQuickFilter] = useState(() => {
    if (navParams.ready === 'true') return 'Ready to Invoice';
    if (navParams.outstanding === 'true') return 'outstanding';
    if (navParams.overdue === 'true') return 'overdue';
    return 'all';
  });
  const [company, setCompany] = useState(null);
  const [jobOrder, setJobOrder] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => { fetchWithAuth(`${API_URL}/api/crm/revenue/config`).then(setConfig).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/crm/revenue?limit=500`)
      .then(d => setAllRecords(d.revenue)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = allRecords;
    if (quickFilter === 'outstanding') rows = rows.filter(r => OPEN_STATUSES.includes(displayStatus(r)) && outstandingOf(r) > 0);
    else if (quickFilter === 'overdue') rows = rows.filter(isOverdue);
    else if (quickFilter !== 'all') rows = rows.filter(r => r.invoice_status === quickFilter);
    if (company) rows = rows.filter(r => r.company_id === company.id);
    if (jobOrder) rows = rows.filter(r => r.job_order_id === jobOrder.id);
    if (dateFrom) rows = rows.filter(r => r.placement_date && r.placement_date >= dateFrom);
    if (dateTo) rows = rows.filter(r => r.placement_date && r.placement_date <= dateTo);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r => [r.company_name, r.job_title, r.candidate_name, r.invoice_number].some(v => (v || '').toLowerCase().includes(q)));
    }
    return rows;
  }, [allRecords, quickFilter, company, jobOrder, search, dateFrom, dateTo]);

  const summary = useMemo(() => {
    const totalFees = allRecords.reduce((s, r) => s + (r.actual_fee || 0), 0);
    const ready = allRecords.filter(r => r.invoice_status === 'Ready to Invoice');
    const readyAmount = ready.reduce((s, r) => s + (r.actual_fee || 0), 0);
    const totalInvoiced = allRecords.reduce((s, r) => s + (r.amount_invoiced || 0), 0);
    const totalCollected = allRecords.reduce((s, r) => s + (r.amount_collected || 0), 0);
    const outstandingAR = allRecords.filter(r => OPEN_STATUSES.includes(displayStatus(r))).reduce((s, r) => s + outstandingOf(r), 0);
    const overdueRows = allRecords.filter(isOverdue);
    const overdueTotal = overdueRows.reduce((s, r) => s + outstandingOf(r), 0);
    return { totalFees, readyCount: ready.length, readyAmount, totalInvoiced, totalCollected, outstandingAR, overdueTotal, overdueCount: overdueRows.length };
  }, [allRecords]);

  return (
    <IntelligenceLayout title="Revenue" embedded={embedded}>
      <div className="p-6 space-y-4" data-testid="crm-revenue-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Revenue</h1>
            <p className="text-sm text-gray-500 mt-1">Placement fees, invoicing &amp; collections — estimated, actual, invoiced and collected are always tracked separately</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-revenue-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 transition-colors">
            <Plus className="h-4 w-4" /> New Revenue Record
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="revenue-summary-cards">
              <MetricCard label="Fees Earned" value={fmt$(summary.totalFees)} subtext="All placements" icon={DollarSign} />
              <MetricCard label="Ready to Invoice" value={fmt$(summary.readyAmount)} subtext={`${summary.readyCount} placement${summary.readyCount === 1 ? '' : 's'}`} icon={CheckCircle2} />
              <MetricCard label="Total Invoiced" value={fmt$(summary.totalInvoiced)} subtext="Billed to clients" icon={FileText} />
              <MetricCard label="Total Collected" value={fmt$(summary.totalCollected)} subtext="Payments received" icon={CheckCircle2} />
              <MetricCard label="Outstanding A/R" value={fmt$(summary.outstandingAR)} subtext="Invoiced, unpaid" icon={Clock} />
              <MetricCard label="Overdue" value={fmt$(summary.overdueTotal)} subtext={`${summary.overdueCount} invoice${summary.overdueCount === 1 ? '' : 's'}`} icon={AlertTriangle} />
            </div>

            <div className="flex items-center flex-wrap gap-2" data-testid="revenue-filter-chips">
              {QUICK_FILTERS.map(f => (
                <button key={f.key} onClick={() => setQuickFilter(f.key)} data-testid={`revenue-filter-chip-${f.key}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    quickFilter === f.key ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#D4AF37]/50'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex items-center flex-wrap gap-2" data-testid="revenue-filters">
              <div className="w-56"><CompanyPicker value={company} onChange={setCompany} testId="revenue-company-picker" /></div>
              <div className="w-56"><JobOrderPicker value={jobOrder} onChange={setJobOrder} testId="revenue-job-order-picker" /></div>
              <div className="relative">
                <SearchIcon className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search candidate, invoice #..." data-testid="revenue-search-input"
                  className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
              </div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="revenue-date-from" title="Placement date from"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="revenue-date-to" title="Placement date to"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 p-8 text-center" data-testid="revenue-empty-state">No revenue records match these filters.</p>
              ) : (
                <table className="w-full text-sm" data-testid="revenue-table">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Company</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Job Title</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Candidate</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Placement Date</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Fee %</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Actual Fee</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Invoice #</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Invoice Date</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Due Date</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Invoiced</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Collected</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Outstanding</th>
                      <th className="text-left py-2.5 px-4 whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => {
                      const bucket = agingBucket(r);
                      return (
                        <tr key={r.id} onClick={() => setSelectedId(r.id)} data-testid={`revenue-row-${r.id}`}
                          className="border-t border-gray-100 hover:bg-[#fdf9ef] cursor-pointer transition-colors">
                          <td className="py-2.5 px-4 font-medium text-[#0A1F44] whitespace-nowrap">{r.company_name || '—'}</td>
                          <td className="py-2.5 px-4 text-gray-600 whitespace-nowrap">{r.job_title || '—'}</td>
                          <td className="py-2.5 px-4 text-gray-600 whitespace-nowrap">{r.candidate_name || '—'}</td>
                          <td className="py-2.5 px-4 text-gray-500 whitespace-nowrap">{r.placement_date || '—'}</td>
                          <td className="py-2.5 px-4 text-gray-500 whitespace-nowrap">{r.agreed_fee_percentage != null ? `${r.agreed_fee_percentage}%` : '—'}</td>
                          <td className="py-2.5 px-4 text-gray-700 font-medium whitespace-nowrap">{r.actual_fee != null ? fmt$(r.actual_fee) : '—'}</td>
                          <td className="py-2.5 px-4 text-gray-500 whitespace-nowrap">{r.invoice_number || '—'}</td>
                          <td className="py-2.5 px-4 text-gray-500 whitespace-nowrap">{r.invoice_date || '—'}</td>
                          <td className="py-2.5 px-4 whitespace-nowrap">
                            <span className={bucket ? 'text-red-600 font-medium' : 'text-gray-500'}>{r.payment_due_date || '—'}</span>
                            {bucket && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700">{bucket}</span>}
                          </td>
                          <td className="py-2.5 px-4 text-gray-600 whitespace-nowrap">{r.amount_invoiced != null ? fmt$(r.amount_invoiced) : '—'}</td>
                          <td className="py-2.5 px-4 text-gray-600 whitespace-nowrap">{r.amount_collected != null ? fmt$(r.amount_collected) : '—'}</td>
                          <td className="py-2.5 px-4 font-medium whitespace-nowrap">{fmt$(outstandingOf(r))}</td>
                          <td className="py-2.5 px-4 whitespace-nowrap"><RevenueStatusBadge record={r} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {showCreate && <CreateRevenueModal config={config} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {selectedId && <RevenueDetailModal revenueId={selectedId} config={config} onClose={() => setSelectedId(null)} onUpdated={load} />}
    </IntelligenceLayout>
  );
};

const CreateRevenueModal = ({ config, onClose, onCreated }) => {
  const [jobOrder, setJobOrder] = useState(null);
  const [form, setForm] = useState({ candidate_name: '', placement_date: '', actual_fee: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!jobOrder) { toast.error('Select a job order'); return; }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/revenue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_order_id: jobOrder.id, candidate_name: form.candidate_name, placement_date: form.placement_date, actual_fee: form.actual_fee ? Number(form.actual_fee) : null }),
      });
      toast.success('Revenue record created');
      onCreated();
    } catch (e) {
      toast.error('Could not create revenue record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="create-revenue-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-2"><DollarSign className="h-4 w-4" /> New Revenue Record</h3>
          <button onClick={onClose} data-testid="close-create-revenue-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <JobOrderPicker value={jobOrder} onChange={setJobOrder} testId="revenue-create-job-order-picker" />
        <input value={form.candidate_name} onChange={e => setForm({ ...form, candidate_name: e.target.value })} placeholder="Candidate name"
          data-testid="revenue-create-candidate-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <input type="date" value={form.placement_date} onChange={e => setForm({ ...form, placement_date: e.target.value })}
          data-testid="revenue-create-placement-date-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <input type="number" value={form.actual_fee} onChange={e => setForm({ ...form, actual_fee: e.target.value })} placeholder="Actual placement fee (optional — defaults to job order estimate)"
          data-testid="revenue-create-actual-fee-input" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <button onClick={submit} disabled={saving} data-testid="create-revenue-submit"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Creating...' : 'Create Revenue Record'}
        </button>
      </div>
    </div>
  );
};

const RevenueDetailModal = ({ revenueId, config, onClose, onUpdated }) => {
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/crm/revenue/${revenueId}`).then(d => { setRecord(d); setForm(d); }).catch(() => {}).finally(() => setLoading(false));
  }, [revenueId]);

  useEffect(() => { load(); }, [load]);

  const persist = async (patch, successMsg) => {
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/revenue/${revenueId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      toast.success(successMsg || 'Revenue record updated');
      load(); onUpdated && onUpdated();
    } catch (e) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const save = () => persist({
    candidate_name: form.candidate_name, placement_date: form.placement_date,
    actual_fee: form.actual_fee !== '' ? Number(form.actual_fee) : null,
    invoice_number: form.invoice_number, invoice_date: form.invoice_date, payment_due_date: form.payment_due_date,
    amount_invoiced: form.amount_invoiced !== '' && form.amount_invoiced != null ? Number(form.amount_invoiced) : null,
    amount_collected: form.amount_collected !== '' && form.amount_collected != null ? Number(form.amount_collected) : null,
    paid_date: form.paid_date, invoice_status: form.invoice_status, notes: form.notes,
  });

  const markReadyToInvoice = () => persist({ invoice_status: 'Ready to Invoice' }, 'Marked Ready to Invoice');

  const recordInvoice = () => {
    if (!form.invoice_number || !form.invoice_date || !form.amount_invoiced) {
      toast.error('Fill in Invoice #, Invoice Date and Invoiced Amount first, then Record Invoice');
      return;
    }
    persist({
      invoice_number: form.invoice_number, invoice_date: form.invoice_date,
      amount_invoiced: Number(form.amount_invoiced), invoice_status: 'Invoiced',
    }, 'Invoice recorded');
  };

  const recordPayment = () => {
    const amt = Number(paymentAmount);
    if (!amt || amt <= 0) { toast.error('Enter a payment amount'); return; }
    const newCollected = (record.amount_collected || 0) + amt;
    const invoiced = record.amount_invoiced || 0;
    const newStatus = invoiced > 0 && newCollected >= invoiced ? 'Paid' : 'Partially Paid';
    persist({ amount_collected: newCollected, paid_date: today(), invoice_status: newStatus }, 'Payment recorded');
    setPaymentAmount('');
  };

  if (loading || !form) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="revenue-detail-modal">
        <Loader2 className="h-6 w-6 animate-spin text-white" />
      </div>
    );
  }

  const outstanding = outstandingOf(form);
  const bucket = agingBucket(record);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-end" data-testid="revenue-detail-modal">
      <div className="bg-white h-full w-full max-w-xl shadow-xl overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#0A1F44]">{record.candidate_name || 'Unnamed placement'}</h3>
            <p className="text-xs text-gray-500">{record.company_name} · {record.job_title}</p>
          </div>
          <button onClick={onClose} data-testid="close-revenue-detail"><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="flex items-center gap-2">
          <RevenueStatusBadge record={record} />
          {bucket && <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700">{bucket} overdue</span>}
        </div>

        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-1" data-testid="revenue-linked-record-info">
          <p><b className="text-[#0A1F44]">Agreement:</b> {record.agreement ? `${record.agreement.status} · ${record.agreement.fee_percentage}% fee · ${record.agreement.payment_terms || 'no terms set'}` : 'No agreement linked'}</p>
          <p><b className="text-[#0A1F44]">Compensation:</b> {record.comp_min ? `${fmt$(record.comp_min)}–${fmt$(record.comp_max)} ${record.comp_currency || ''}` : '—'}</p>
          <p><b className="text-[#0A1F44]">Expected Fee (est.):</b> {record.estimated_fee ? `${fmt$(record.estimated_fee.min)}–${fmt$(record.estimated_fee.max)}` : 'Not available'}</p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="revenue-quick-actions">
          <button onClick={markReadyToInvoice} disabled={saving} data-testid="mark-ready-to-invoice-btn"
            className="text-xs px-3 py-2 border border-gray-200 rounded-lg text-[#0A1F44] hover:border-[#D4AF37]/50 disabled:opacity-50">Mark Ready to Invoice</button>
          <button onClick={recordInvoice} disabled={saving} data-testid="record-invoice-btn"
            className="text-xs px-3 py-2 border border-gray-200 rounded-lg text-[#0A1F44] hover:border-[#D4AF37]/50 disabled:opacity-50">Record Invoice</button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs font-medium text-gray-500">Candidate</label>
            <input value={form.candidate_name || ''} onChange={e => setForm({ ...form, candidate_name: e.target.value })} data-testid="revenue-detail-candidate" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Placement Date</label>
            <input type="date" value={form.placement_date || ''} onChange={e => setForm({ ...form, placement_date: e.target.value })} data-testid="revenue-detail-placement-date" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Fee %</label>
            <input value={form.agreed_fee_percentage ?? ''} disabled data-testid="revenue-detail-fee-pct" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500" /></div>
          <div><label className="text-xs font-medium text-gray-500">Actual Fee</label>
            <input type="number" value={form.actual_fee ?? ''} onChange={e => setForm({ ...form, actual_fee: e.target.value })} data-testid="revenue-detail-actual-fee" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
        </div>

        <div className="border-t border-gray-100 pt-3 grid grid-cols-2 gap-2">
          <div><label className="text-xs font-medium text-gray-500">Invoice #</label>
            <input value={form.invoice_number || ''} onChange={e => setForm({ ...form, invoice_number: e.target.value })} data-testid="revenue-detail-invoice-number" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Invoice Status</label>
            <select value={form.invoice_status || 'Not Ready'} onChange={e => setForm({ ...form, invoice_status: e.target.value })} data-testid="revenue-detail-status"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
              {config.invoice_statuses.map(s => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
            </select></div>
          <div><label className="text-xs font-medium text-gray-500">Invoice Date</label>
            <input type="date" value={form.invoice_date || ''} onChange={e => setForm({ ...form, invoice_date: e.target.value })} data-testid="revenue-detail-invoice-date" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Due Date</label>
            <input type="date" value={form.payment_due_date || ''} onChange={e => setForm({ ...form, payment_due_date: e.target.value })} data-testid="revenue-detail-due-date" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Invoiced Amount</label>
            <input type="number" value={form.amount_invoiced ?? ''} onChange={e => setForm({ ...form, amount_invoiced: e.target.value })} data-testid="revenue-detail-amount-invoiced" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Collected Amount</label>
            <input type="number" value={form.amount_collected ?? ''} onChange={e => setForm({ ...form, amount_collected: e.target.value })} data-testid="revenue-detail-amount-collected" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Last Paid Date</label>
            <input type="date" value={form.paid_date || ''} onChange={e => setForm({ ...form, paid_date: e.target.value })} data-testid="revenue-detail-paid-date" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>
          <div><label className="text-xs font-medium text-gray-500">Outstanding Balance</label>
            <input value={fmt$(outstanding)} disabled data-testid="revenue-detail-outstanding" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium" /></div>
        </div>

        <div className="border-t border-gray-100 pt-3 space-y-2" data-testid="revenue-record-payment-section">
          <label className="text-xs font-medium text-gray-500">Record a Payment</label>
          <div className="flex gap-2">
            <input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="Payment amount"
              data-testid="revenue-payment-amount-input" className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            <button onClick={recordPayment} disabled={saving} data-testid="record-payment-btn"
              className="px-3 py-2 text-xs font-medium bg-[#0A1F44] text-white rounded-lg hover:bg-[#0A1F44]/90 disabled:opacity-50">Record Payment</button>
          </div>
        </div>

        <div><label className="text-xs font-medium text-gray-500">Internal Notes</label>
          <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="revenue-detail-notes" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" /></div>

        <button onClick={save} disabled={saving} data-testid="save-revenue-detail"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default CRMRevenue;
